import { assertReportAdmission, createNativePredicateSession, NativeConditionError, validateConditionalDefinition } from "./nativeConditions.mjs";
import { createNativeStyleResolver } from "./nativeStyles.mjs";
import { inheritNativeTypedExecution, nativeTypedExecution } from "./nativeTypedExecution.mjs";

const permits = new WeakMap();
const executions = new WeakMap();
export const VISIBILITY_SUPPRESSION_NOTICE = "Some detail records are hidden; totals include all records.";

// Programmatic harness only. No request/header/definition flag can manufacture a permit.
function validateSource(payload, formatting = false) {
  const admission = assertReportAdmission(payload, "validate");
  if (!admission.requiredFeatures.includes("conditional-rules-v1") && admission.requiredFeatures.some(feature => feature !== "conditional-rules-v1")) throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "A typed-only definition cannot execute through a conditional permit.");
  if (admission.requiredFeatures.some(feature => !["conditional-rules-v1", "typed-expressions-v1", "report-parameters-v1"].includes(feature))) {
    throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Internal conditional execution does not admit unknown features.");
  }
  const validation = validateConditionalDefinition(payload);
  if (validation.errors.length) {
    const error = validation.errors[0];
    throw new NativeConditionError(error.code, error.path, error.message, error);
  }
  function scan(value) {
    if (!value || typeof value !== "object") return;
    if (value.conditions?.formatting?.length) throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.layout", "Conditional formatting is not implemented. Visibility-only internal execution cannot render formatting rules.");
    Object.values(value).forEach(scan);
  }
  if (!formatting) scan(payload.layout);
}
export function createInternalVisibilityExecution(payload) {
  validateSource(payload);
  const permit = Object.freeze({});
  permits.set(permit, { payload, formatting: false });
  return permit;
}

export function createInternalFormattingExecution(payload) {
  validateSource(payload, true);
  const permit = Object.freeze({});
  permits.set(permit, { payload, formatting: true });
  return permit;
}

export function prepareNativeVisibility(payload, permit) {
  if (permit === undefined) {
    if (nativeTypedExecution(payload) && payload.layout.requiredFeatures?.includes("conditional-rules-v1")) throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.visibilityExecution", "Combined profiles require their separately owned conditional execution permit.");
    admitNativeExecution(payload);
    return payload;
  }
  const admission = permits.get(permit);
  if (admission?.payload !== (nativeTypedExecution(payload)?.source || payload)) throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.visibilityExecution", "Invalid internal visibility execution.");
  if (payload.layout.requiredFeatures?.some(feature => ["typed-expressions-v1", "report-parameters-v1"].includes(feature)) && !nativeTypedExecution(payload)) throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.typedExecution", "Combined profiles require their separately owned typed execution permit.");
  validateSource(payload, admission.formatting);
  const prepared = { ...payload };
  inheritNativeTypedExecution(payload, prepared);
  const session = createNativePredicateSession();
  const styles = createNativeStyleResolver(session);
  const decisions = new Map();
  const tables = new Map();
  const notices = new Set();
  const scopeNames = ["document", "dataset", "group", "detail", "cell"];
  const scopeCache = new Map();
  function contextScopes(context) {
    const mask = scopeNames.reduce((mask, scope, index) => mask | (context[scope] !== undefined ? 1 << index : 0), 0);
    if (!scopeCache.has(mask)) scopeCache.set(mask, Object.freeze(scopeNames.filter((_, index) => mask & (1 << index))));
    return scopeCache.get(mask);
  }
  executions.set(prepared, {
    tables, notices, decisions, formatting: admission.formatting,
    context: (row, dataset) => ({
      document: payload.datasets,
      ...(row !== undefined ? { detail: row } : {}),
      ...(dataset !== undefined ? { dataset: dataset && !Array.isArray(dataset) ? dataset : {} } : {}),
    }),
    visible(target, context, identity) {
      if (decisions.has(identity)) return decisions.get(identity).reportVisible;
      const rule = target?.conditions?.visibleWhen;
      const matches = !rule || rule.enabled === false || session.evaluate(rule.when, context, {
        ruleId: rule.id, elementId: target.id, sourceIndex: context.sourceIndex,
      }).value;
      const reportVisible = target?.visible !== false && target?.hidden !== true && matches;
      decisions.set(identity, Object.freeze({
        identity, reportVisible, ruleId: rule?.id,
        visibilityMatched: rule && rule.enabled !== false ? matches : undefined,
        ...(target?.conditions ? { scopes: contextScopes(context) } : {}),
        ...(admission.formatting ? styles.overrides(target, context) : {}),
      }));
      return reportVisible;
    },
    style(target, context, identity, base, broadIdentity) {
      this.visible(target, context, identity);
      const decision = decisions.get(identity);
      const broad = decisions.get(broadIdentity);
      const resolvedStyle = styles.resolve(base, broad?.styleOverrides, decision.styleOverrides);
      const matchedRuleIds = Object.freeze([...(broad?.styleRuleIds || []), ...decision.styleRuleIds]);
      decisions.set(identity, Object.freeze({ ...decision, resolvedStyle, matchedRuleIds }));
      return { style: resolvedStyle, conditionalStyle: true, matchedRuleIds };
    },
    get work() { return session.work; },
    get styleCount() { return styles.size; },
  });
  return prepared;
}

export function nativeVisibility(payload) { return executions.get(payload); }
export function admitNativeExecution(payload) {
  return assertReportAdmission(payload, nativeVisibility(payload) || nativeTypedExecution(payload) ? "validate" : "render");
}
